import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import ShareModal from "@/src/components/action-modals/share-modal";
import { useAuth } from "@/src/contexts/auth.context";
import useLgMediaQuery from "@/src/hooks/media-queries/use-lg-media-query";
import useMdMediaQuery from "@/src/hooks/media-queries/use-md-media-query";
import { BlocksApiArticle } from "@/src/models/wiki.models";
import {
  resolveAuthorName,
  useToggleWikiSaved,
  useWikiArticlesByCategory,
  useWikiDeleteArticle,
  useWikiSaveState,
} from "@/src/services/wiki/wiki.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { stripHtml } from "@/src/utils/html.utils";
import { LinearGradient } from "expo-linear-gradient";
import { router } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FlatList,
  Image,
  LayoutChangeEvent,
  Platform,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import DropDown from "../../ui-kit/drop-down";
import BlocksAvatar from "../../ui-kit/blocks-avatar";
import WikiArticleCard from "../wiki-article-card/wiki-article-card";
import WikiCardSkeleton from "../wiki-card-skeleton";

const isWeb = Platform.OS === "web";
const CARD_GAP = 16;
const VISIBLE_CARDS = isWeb ? 3 : 2.5;

type WebArticleCardProps = Readonly<{
  article: BlocksApiArticle;
  onPress: (article: BlocksApiArticle) => void;
  onShare: (article: BlocksApiArticle) => void;
  onDelete: (article: BlocksApiArticle) => void;
  onToggleSave: (article: BlocksApiArticle) => void;
  isSaved: boolean;
  cardWidth?: number;
}>;

function WebArticleCard({
  article,
  onPress,
  onShare,
  onDelete,
  onToggleSave,
  isSaved,
  cardWidth,
}: WebArticleCardProps) {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [isCardHovered, setIsCardHovered] = useState(false);

  const authorName = resolveAuthorName(article);
  const formattedDate = article.CreatedDate
    ? new Date(article.CreatedDate).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    : "";

  const dropdownItems = [
    {
      label: isSaved ? t("BTN_UNSAVE") : t("BTN_SAVE"),
      onPress: () => onToggleSave(article),
    },
    {
      label: t("BTN_UPDATE"),
      onPress: () =>
        router.push({
          pathname: "/(tabs)/wiki/wiki-creation",
          params: {
            draftId: article.ItemId,
            Title: article.Title,
            Content: article.Content,
            categoryId: article.categoryIds ?? "",
            coverImageUrl: article.coverImageUrl ?? "",
            attachments: JSON.stringify(
              (article.attachmentUrls ?? []).map((att) => ({
                id: att.id,
                name: att.name,
                size: att.size,
                url: att.url,
                mimeType: att.mimeType,
              })),
            ),
          },
        }),
    },
    { label: t("LABEL_SHARE"), onPress: () => onShare(article) },
    {
      label: t("BTN_DELETE"),
      onPress: () => onDelete(article),
      destructive: true,
    },
  ];

  return (
    <Pressable
      onPress={() => onPress(article)}
      onHoverIn={() => setIsCardHovered(true)}
      onHoverOut={() => setIsCardHovered(false)}
      className="rounded-2xl overflow-hidden p-4 flex-1 flex-col bg-chalk"
      style={{
        width: cardWidth ?? "100%",
        borderWidth: 1,
        borderColor: isCardHovered
          ? themeVars["--color-primary-default"]
          : themeVars["--color-ash-3"],
      }}
    >
      {article.coverImageUrl && (
        <View className="rounded-lg overflow-hidden">
          <Image
            source={{ uri: article.coverImageUrl }}
            style={{ width: "100%", height: 300 }}
            resizeMode="cover"
          />
        </View>
      )}

      <View className="flex-1">
        <View className="flex-row items-center justify-between">
          <Text
            className="mt-2 text-base font-semibold flex-1 text-onyx"
            numberOfLines={1}
          >
            {article.Title}
          </Text>
          <DropDown items={dropdownItems} />
        </View>
        <Text className="mt-2 text-sm leading-5 text-slate" numberOfLines={6}>
          {stripHtml(article.Content)}
        </Text>
        <LinearGradient
          colors={["transparent", themeVars["--color-chalk-default"] as string]}
          className="absolute bottom-0 left-0 right-0 h-6"
        />
      </View>

      <View className="mt-auto pt-4 flex-row items-center justify-between gap-2">
        <View className="flex-row gap-2 items-center flex-1 overflow-hidden">
          <BlocksAvatar
            userId={article.CreatedBy}
            fallbackImageUrl={article.authorSnapshot?.profileImageUrl}
            size={20}
          />
          <Text className="text-xs font-medium text-ash-1" numberOfLines={1}>
            {authorName}
          </Text>
        </View>
        <Text className="text-xs text-ash-1" numberOfLines={1}>
          {formattedDate}
        </Text>
      </View>
    </Pressable>
  );
}

type WikiCategoryArticlesProps = Readonly<{
  categoryId: string;
  searchText?: string;
  onArticlePress?: (article: BlocksApiArticle) => void;
}>;

export default function WikiCategoryArticles({
  categoryId,
  searchText,
  onArticlePress,
}: WikiCategoryArticlesProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const isMd = useMdMediaQuery();
  const isLg = useLgMediaQuery();

  const [containerWidth, setContainerWidth] = useState(0);
  const [shareArticle, setShareArticle] = useState<BlocksApiArticle | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<BlocksApiArticle | null>(
    null,
  );
  const [localDeletedIds, setLocalDeletedIds] = useState<string[]>([]);
  const [showDeleteConfirmId, setShowDeleteConfirmId] = useState<string | null>(
    null,
  );
  const [showShareModalId, setShowShareModalId] = useState<string | null>(null);

  const { isSaved } = useWikiSaveState();
  const { mutate: toggleSave } = useToggleWikiSaved(userId);
  const {
    data: allArticles = [],
    isLoading,
    isError,
  } = useWikiArticlesByCategory(categoryId, searchText);
  const { mutate: deleteArticleMutation } = useWikiDeleteArticle({
    categoryId,
  });

  const articles = allArticles.filter(
    (article) => !localDeletedIds.includes(article.ItemId),
  );

  const handleArticlePress = (article: BlocksApiArticle) => {
    if (onArticlePress) {
      onArticlePress(article);
    } else {
      router.push({
        pathname: "/(tabs)/wiki/wiki-article-detail",
        params: { articleId: article.ItemId },
      });
    }
  };

  const handleLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const effectiveWidth = containerWidth > 0 ? containerWidth : 800;
  const cardWidth =
    (effectiveWidth - CARD_GAP * (Math.ceil(VISIBLE_CARDS) - 1)) /
    VISIBLE_CARDS;
  const scrollWidth =
    articles.length * cardWidth +
    Math.max(0, articles.length - 1) * CARD_GAP +
    8;
  const showFiller = articles.length <= 2;

  if (isLoading) {
    if (isMobile) {
      return (
        <View className="flex-1 gap-3 pt-1">
          {Array.from({ length: 3 }).map((_, index) => (
            <WikiCardSkeleton key={index} />
          ))}
        </View>
      );
    }
    return (
      <View
        style={{ flex: 1, flexDirection: "row", gap: CARD_GAP }}
        className="px-1 py-1"
        onLayout={handleLayout}
      >
        {Array.from({ length: Math.ceil(VISIBLE_CARDS) }).map((_, index) => (
          <WikiCardSkeleton key={index} width={cardWidth} />
        ))}
      </View>
    );
  }

  if (isError) {
    return (
      <View className="flex-1 items-center justify-center">
        <Text className="text-ash-1">{t("ERROR_LOADING_ARTICLES")}</Text>
      </View>
    );
  }

  if (articles.length === 0) {
    return (
      <View className="flex-1 items-center justify-center gap-4">
        <Image
          source={require("@/src/assets/images/empty-state.png")}
          style={{ width: 100, height: 100 }}
          resizeMode="contain"
        />
        <Text className="text-ash-1">
          {t(searchText?.trim() ? "HINT_WIKI_FILTER" : "LABEL_NO_ARTICLES")}
        </Text>
      </View>
    );
  }

  if (!isLg) {
    const numColumns = isLg ? 3 : isMd ? 2 : 1;
    const itemWidthClass =
      numColumns === 3 ? "w-[32.4%]" : numColumns === 2 ? "w-[49%]" : "w-full";
    const columnWrapperStyle = numColumns > 1 ? { gap: 10 } : undefined;

    const mobileDropdownItems = (article: BlocksApiArticle) => [
      {
        label: isSaved(article.ItemId) ? t("BTN_UNSAVE") : t("BTN_SAVE"),
        onPress: () => toggleSave(article.ItemId),
      },
      {
        label: t("BTN_UPDATE"),
        onPress: () =>
          router.push({
            pathname: "/(tabs)/wiki/wiki-creation",
            params: {
              draftId: article.ItemId,
              Title: article.Title,
              Content: article.Content,
              categoryId: article.categoryIds ?? "",
              coverImageUrl: article.coverImageUrl ?? "",
              attachments: JSON.stringify(
                (article.attachmentUrls ?? []).map((att) => ({
                  id: att.id,
                  name: att.name,
                  size: att.size,
                  url: att.url,
                  mimeType: att.mimeType,
                })),
              ),
            },
          }),
      },
      {
        label: t("LABEL_SHARE"),
        onPress: () => setShowShareModalId(article.ItemId),
      },
      {
        label: t("BTN_DELETE"),
        onPress: () => setShowDeleteConfirmId(article.ItemId),
        destructive: true,
      },
    ];

    return (
      <>
        <FlatList
          key={numColumns}
          data={articles}
          keyExtractor={(item) => item.ItemId}
          numColumns={numColumns}
          columnWrapperStyle={columnWrapperStyle}
          contentContainerStyle={{ gap: 12 }}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }) => (
            <>
              <ConfirmModal
                visible={showDeleteConfirmId === item.ItemId}
                title="BTN_DELETE"
                description="LABEL_DESC_DELETE_WIKI"
                confirmText={t("BTN_DELETE")}
                cancelText={t("BTN_CANCEL")}
                onConfirm={() => {
                  setLocalDeletedIds((prev) => [...prev, item.ItemId]);
                  deleteArticleMutation(item.ItemId);
                  setShowDeleteConfirmId(null);
                }}
                onCancel={() => setShowDeleteConfirmId(null)}
              />
              <WikiArticleCard
                article={item}
                itemWidthClass={itemWidthClass}
                dropdownItems={mobileDropdownItems(item)}
                onPress={handleArticlePress}
                showShareModal={showShareModalId === item.ItemId}
                onShareModalClose={() => setShowShareModalId(null)}
              />
            </>
          )}
        />
      </>
    );
  }

  return (
    <>
      <View style={{ flex: 1, flexDirection: "row" }} onLayout={handleLayout}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{
            width: showFiller ? scrollWidth : undefined,
            flexGrow: showFiller ? 0 : 1,
          }}
          contentContainerStyle={{
            gap: CARD_GAP,
            paddingHorizontal: 4,
            paddingVertical: 4,
          }}
        >
          {articles.map((article) => (
            <WebArticleCard
              key={article.ItemId}
              article={article}
              cardWidth={cardWidth}
              isSaved={isSaved(article.ItemId)}
              onPress={handleArticlePress}
              onShare={(a) => setShareArticle(a)}
              onDelete={(a) => setDeleteTarget(a)}
              onToggleSave={(a) => toggleSave(a.ItemId)}
            />
          ))}
        </ScrollView>

        {showFiller && containerWidth > 0 && (
          <View
            className="items-center justify-center gap-3"
            style={{ width: containerWidth - scrollWidth }}
          >
            <Image
              source={require("@/src/assets/images/empty-state.png")}
              style={{ width: 80, height: 80 }}
              resizeMode="contain"
            />
            <Text className="text-center text-sm text-ash-1">
              {t("LABEL_NO_MORE_ARTICLES")}
            </Text>
          </View>
        )}
      </View>

      <ConfirmModal
        visible={deleteTarget !== null}
        title="BTN_DELETE"
        description="LABEL_DESC_DELETE_WIKI"
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        onConfirm={() => {
          if (!deleteTarget) return;
          setLocalDeletedIds((prev) => [...prev, deleteTarget.ItemId]);
          deleteArticleMutation(deleteTarget.ItemId);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      <ShareModal
        visible={shareArticle !== null}
        onClose={() => setShareArticle(null)}
        resourceId={shareArticle?.ItemId ?? ""}
        resourcePath="wiki"
        title={shareArticle?.Title}
      />
    </>
  );
}
